import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import AnalyseChecklist, { CATEGORIES_ANALYSES } from '@/components/feature/AnalyseChecklist';
import { resolveAnalysesToCatalogueIds, CatalogueEntry } from '@/lib/analyseMatcher';

export interface BonLaboConstantes {
  taille: string;
  poids: string;
  tension_arterielle: string;
  temperature: string;
  libelle: string;
}

export interface BonLaboSoin {
  id: string;
  date: string;
  soin: string;
  tarif: string;
}

export interface BonLaboCreatedData {
  id: string;
  patientName: string;
  practitionerName: string;
  practitionerTitle: string;
  analyses: string[];
  description: string;
  urgences: boolean;
  observations: string;
  constantes: BonLaboConstantes;
  soins: BonLaboSoin[];
  patientAge: string;
  patientTelephone: string;
  patientProfession: string;
  patientAssure: boolean;
  medecinTraitant: string;
  antecedentsMedicaux: string;
}

interface BonLaboratoireSectionProps {
  patientId: string;
  medecinId: string;
  consultationId: string | null;
  suiteTerminee: boolean;
  onCreated: (data: BonLaboCreatedData) => void;
}

function calculerAge(dateNaissance: string | null): string {
  if (!dateNaissance) return '—';
  const naissance = new Date(dateNaissance);
  const aujourdhui = new Date();
  let age = aujourdhui.getFullYear() - naissance.getFullYear();
  const mois = aujourdhui.getMonth() - naissance.getMonth();
  if (mois < 0 || (mois === 0 && aujourdhui.getDate() < naissance.getDate())) {
    age--;
  }
  if (age < 2) {
    const moisBebe = Math.max(
      0,
      (aujourdhui.getFullYear() - naissance.getFullYear()) * 12 + aujourdhui.getMonth() - naissance.getMonth()
    );
    return `${moisBebe} mois`;
  }
  return `${age} ans`;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent';

export default function BonLaboratoireSection({
  patientId,
  medecinId,
  consultationId,
  suiteTerminee,
  onCreated,
}: BonLaboratoireSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [patient, setPatient] = useState<{
    nom: string;
    prenom: string;
    date_naissance: string | null;
    telephone: string | null;
    antecedents_medicaux: string | null;
  } | null>(null);
  const [practitionerName, setPractitionerName] = useState('');
  const [practitionerTitle, setPractitionerTitle] = useState('');

  const [selectedAnalyses, setSelectedAnalyses] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [urgences, setUrgences] = useState(false);
  const [observationsCliniques, setObservationsCliniques] = useState('');
  const [dbCustomAnalyses, setDbCustomAnalyses] = useState<{ nom: string; items: { id: string; label: string }[] }[]>([]);
  const [catalogueEntries, setCatalogueEntries] = useState<CatalogueEntry[]>([]);

  const [patientTelephone, setPatientTelephone] = useState('');
  const [patientProfession, setPatientProfession] = useState('');
  const [patientAssure, setPatientAssure] = useState(false);
  const [medecinTraitant, setMedecinTraitant] = useState('');

  const [constantes, setConstantes] = useState<BonLaboConstantes>({
    taille: '',
    poids: '',
    tension_arterielle: '',
    temperature: '',
    libelle: '',
  });

  const [soins, setSoins] = useState<BonLaboSoin[]>([{ id: crypto.randomUUID(), date: '', soin: '', tarif: '' }]);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!showForm) return;
    loadFormData();
  }, [showForm, patientId, medecinId]);

  const loadFormData = async () => {
    try {
      const { data: patientData } = await supabase
        .from('patients')
        .select('nom, prenom, date_naissance, telephone, antecedents_medicaux')
        .eq('id', patientId)
        .single();

      if (patientData) {
        setPatient(patientData);
        if (patientData.telephone) setPatientTelephone(patientData.telephone);
      }

      const { data: medecinData } = await supabase
        .from('medecins')
        .select('nom, prenom, specialite')
        .eq('id', medecinId)
        .single();

      if (medecinData) {
        setPractitionerName(`Dr. ${medecinData.prenom} ${medecinData.nom}`);
        setPractitionerTitle(medecinData.specialite || '');
        setMedecinTraitant(`Dr. ${medecinData.prenom} ${medecinData.nom}`);
      }

      // Analyses personnalisées depuis la base
      const { data: custom } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('nom')
        .eq('categorie', 'Analyses personnalisées')
        .order('nom');
      if (custom && custom.length > 0) {
        setDbCustomAnalyses([
          { nom: 'Analyses personnalisées', items: custom.map((a: any) => ({ id: a.nom, label: a.nom })) },
        ]);
      }

      // Catalogue complet pour le matching
      const { data: catalogue } = await supabase
        .from('analyses_biochimie_catalogue')
        .select('id, code, nom');
      if (catalogue) setCatalogueEntries(catalogue as CatalogueEntry[]);
    } catch (err) {
      console.error('Erreur chargement bon laboratoire:', err);
    }
  };

  const toggleAnalyse = (id: string) =>
    setSelectedAnalyses((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const handleConstanteChange = (field: keyof BonLaboConstantes, value: string) =>
    setConstantes((prev) => ({ ...prev, [field]: value }));

  const handleAddSoinRow = () =>
    setSoins((prev) => [...prev, { id: crypto.randomUUID(), date: '', soin: '', tarif: '' }]);

  const handleRemoveSoinRow = (id: string) => {
    if (soins.length > 1) setSoins((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSoinChange = (id: string, field: keyof BonLaboSoin, value: string) =>
    setSoins((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  const getAnalysesLabels = () => {
    const allItems = CATEGORIES_ANALYSES.flatMap((c) => c.items).concat(dbCustomAnalyses.flatMap((c) => c.items));
    return selectedAnalyses.map((id) => {
      const item = allItems.find((i) => i.id === id);
      return item ? item.label : id;
    });
  };

  const handleSave = async () => {
    if (selectedAnalyses.length === 0) {
      setErrorMsg('Sélectionnez au moins une analyse avant de créer le bon.');
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      const analysesLabels = getAnalysesLabels();
      const typeAnalyseStr = analysesLabels.join(' ; ');

      let matchedIds: string[] = [];
      try {
        matchedIds = (await resolveAnalysesToCatalogueIds(analysesLabels, catalogueEntries)).ids;
      } catch (err) {
        console.error('Erreur résolution catalogue:', err);
      }

      const { data: bon, error } = await supabase
        .from('bons_laboratoire')
        .insert({
          patient_id: patientId,
          medecin_id: medecinId,
          type_analyse: typeAnalyseStr,
          description: description.trim() || null,
          urgences,
          observations_cliniques: observationsCliniques.trim() || null,
          statut: 'en_attente',
          date_prescription: new Date().toISOString().split('T')[0],
          taille: constantes.taille.trim() || null,
          poids: constantes.poids.trim() || null,
          tension_arterielle: constantes.tension_arterielle.trim() || null,
          temperature: constantes.temperature.trim() || null,
          libelle_constantes: constantes.libelle.trim() || null,
          patient_profession: patientProfession.trim() || null,
          patient_assure: patientAssure,
          patient_telephone: patientTelephone.trim() || null,
          medecin_traitant: medecinTraitant.trim() || null,
          consultation_id: consultationId || null,
          analyses_ids: matchedIds.length > 0 ? matchedIds : null,
        })
        .select()
        .single();

      if (error) throw error;

      // Notifier les laborantins
      try {
        const { data: laborantins } = await supabase
          .from('laborantins')
          .select('email, prenom, nom')
          .eq('bloque', false);
        if (laborantins && laborantins.length > 0) {
          const patientName = patient ? `${patient.prenom} ${patient.nom}` : 'Patient';
          const urgenceLabel = urgences ? 'URGENT - ' : '';
          for (const labo of laborantins) {
            if (labo.email) {
              await supabase.functions.invoke('send-email', {
                body: {
                  to: labo.email,
                  toName: `${labo.prenom} ${labo.nom}`,
                  subject: `${urgenceLabel}Nouvelles analyses - ${patientName}`,
                  htmlContent: '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:40px;font-family:Arial,sans-serif;background:#f5f5f5;"><div style="max-width:600px;margin:0 auto;background:white;padding:40px;border-radius:10px;"><h1 style="color:' + (urgences ? '#ef4444' : '#10B981') + ';margin-bottom:20px;">' + (urgences ? 'Analyses urgentes' : 'Nouvelles analyses') + '</h1><p>Bonjour <strong>' + labo.prenom + '</strong>,</p><p>Un nouveau bon de laboratoire a été créé pour <strong>' + patientName + '</strong>.</p><ul style="margin:20px 0;padding-left:20px;">' + analysesLabels.map((l) => '<li>' + l + '</li>').join('') + '</ul>' + (urgences ? '<div style="background:#fef2f2;border:2px solid #ef4444;padding:15px;border-radius:8px;text-align:center;"><p style="color:#dc2626;font-weight:bold;margin:0;">ANALYSE URGENTE</p></div>' : '') + '</div></body></html>',
                },
              });
            }
          }
        }
      } catch (notifError) {
        console.error('Erreur envoi notifications laborantins:', notifError);
      }

      const agePatient = calculerAge(patient?.date_naissance || null);

      onCreated({
        id: bon.id,
        patientName: patient ? `${patient.prenom} ${patient.nom}` : '',
        practitionerName,
        practitionerTitle,
        analyses: analysesLabels,
        description: description.trim(),
        urgences,
        observations: observationsCliniques.trim(),
        constantes,
        soins,
        patientAge: agePatient,
        patientTelephone,
        patientProfession,
        patientAssure,
        medecinTraitant,
        antecedentsMedicaux: patient?.antecedents_medicaux || '',
      });

      // Réinitialiser le formulaire
      setShowForm(false);
      setSelectedAnalyses([]);
      setUrgences(false);
      setDescription('');
      setObservationsCliniques('');
      setConstantes({ taille: '', poids: '', tension_arterielle: '', temperature: '', libelle: '' });
      setSoins([{ id: crypto.randomUUID(), date: '', soin: '', tarif: '' }]);
    } catch (err: any) {
      setErrorMsg(err.message || 'Erreur lors de la création du bon de laboratoire.');
    } finally {
      setSaving(false);
    }
  };

  const agePatient = calculerAge(patient?.date_naissance || null);

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Bon de laboratoire
        </h2>
        {!suiteTerminee && (
          <button
            onClick={() => { setShowForm(!showForm); setErrorMsg(null); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
              showForm ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-clinic-blue text-white hover:bg-clinic-blue-dark'
            }`}
          >
            <i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i>
            {showForm ? 'Annuler' : 'Créer un bon de laboratoire'}
          </button>
        )}
      </div>

      <div className="p-6">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2 mb-4">
            <i className="ri-error-warning-line flex-shrink-0"></i>{errorMsg}
          </div>
        )}

        {showForm ? (
          <div className="space-y-6">
            {/* Constantes vitales */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-heart-pulse-line text-clinic-blue"></i>
                Constantes vitales du jour
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left">
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Taille (cm)</th>
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Poids (kg)</th>
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">T.A. (mmHg)</th>
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Temp. (°C)</th>
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Libellé</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-2 py-1.5">
                        <input type="text" value={constantes.taille} onChange={(e) => handleConstanteChange('taille', e.target.value)} placeholder="170" className={inputClass} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={constantes.poids} onChange={(e) => handleConstanteChange('poids', e.target.value)} placeholder="68" className={inputClass} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={constantes.tension_arterielle} onChange={(e) => handleConstanteChange('tension_arterielle', e.target.value)} placeholder="12/8" className={inputClass} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={constantes.temperature} onChange={(e) => handleConstanteChange('temperature', e.target.value)} placeholder="37.2" className={inputClass} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input type="text" value={constantes.libelle} onChange={(e) => handleConstanteChange('libelle', e.target.value)} placeholder="Observations..." className={inputClass} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Informations patient */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-user-line text-clinic-blue"></i>
                Informations du patient
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom</label>
                  <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm text-gray-900">{patient?.nom || '—'}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Prénom</label>
                  <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm text-gray-900">{patient?.prenom || '—'}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Âge</label>
                  <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md text-sm text-gray-900">{agePatient}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">N° Téléphone</label>
                  <input type="tel" value={patientTelephone} onChange={(e) => setPatientTelephone(e.target.value)} placeholder="+229 XX XX XX XX" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Profession</label>
                  <input type="text" value={patientProfession} onChange={(e) => setPatientProfession(e.target.value)} placeholder="Profession du patient" className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Assuré</label>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="bon-assure" checked={patientAssure} onChange={() => setPatientAssure(true)} className="w-4 h-4 text-clinic-blue focus:ring-clinic-blue cursor-pointer" />
                      <span className="text-sm text-gray-700">Oui</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="bon-assure" checked={!patientAssure} onChange={() => setPatientAssure(false)} className="w-4 h-4 text-clinic-blue focus:ring-clinic-blue cursor-pointer" />
                      <span className="text-sm text-gray-700">Non</span>
                    </label>
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Médecin traitant</label>
                  <input type="text" value={medecinTraitant} onChange={(e) => setMedecinTraitant(e.target.value)} placeholder="Nom du médecin traitant" className={inputClass} />
                </div>
              </div>
            </div>

            {/* Antécédents médicaux */}
            {patient?.antecedents_medicaux && (
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-file-history-line text-clinic-blue"></i>
                  Antécédents médicaux
                </h3>
                <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                  {patient.antecedents_medicaux}
                </div>
              </div>
            )}

            {/* Checklist des analyses */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Analyses demandées <span className="text-red-500">*</span>
              </label>
              <AnalyseChecklist
                selectedIds={selectedAnalyses}
                onToggle={toggleAnalyse}
                accentColor="blue"
                extraCategories={dbCustomAnalyses}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description détaillée <span className="text-gray-400">(facultatif)</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Précisions sur les analyses demandées, paramètres spécifiques..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none text-sm"
              />
            </div>

            {/* Observations cliniques */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Observations cliniques <span className="text-gray-400">(facultatif)</span>
              </label>
              <textarea
                value={observationsCliniques}
                onChange={(e) => setObservationsCliniques(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder="Signes cliniques, éléments pouvant influencer les résultats..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none text-sm"
              />
            </div>

            {/* Urgence */}
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <input
                type="checkbox"
                id="bon-urgences"
                checked={urgences}
                onChange={(e) => setUrgences(e.target.checked)}
                className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500 cursor-pointer"
              />
              <label htmlFor="bon-urgences" className="flex items-center gap-2 text-sm font-medium text-amber-800 cursor-pointer">
                <i className="ri-alert-line text-lg"></i>
                Analyse urgente (à traiter en priorité par le laboratoire)
              </label>
            </div>

            {/* Tableau des soins associés */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">
                  Soins associés <span className="text-gray-500">(facultatif)</span>
                </label>
                <button
                  type="button"
                  onClick={handleAddSoinRow}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-clinic-blue hover:bg-clinic-blue/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>Ajouter un soin
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="text-left">
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[170px]">Date</th>
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Soin</th>
                      <th className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-[120px]">Tarif (FCFA)</th>
                      <th className="px-2 py-1.5 w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {soins.map((soin) => (
                      <tr key={soin.id}>
                        <td className="px-2 py-1.5">
                          <input
                            type="date"
                            value={soin.date}
                            onChange={(e) => handleSoinChange(soin.id, 'date', e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className={inputClass}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="text" value={soin.soin} onChange={(e) => handleSoinChange(soin.id, 'soin', e.target.value)} placeholder="Nom du soin..." className={inputClass} />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" value={soin.tarif} onChange={(e) => handleSoinChange(soin.id, 'tarif', e.target.value)} placeholder="0" min="0" className={inputClass} />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {soins.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSoinRow(soin.id)}
                              className="w-8 h-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bouton de création */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || selectedAnalyses.length === 0}
                className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Créer &amp; imprimer le bon ({selectedAnalyses.length} analyse{selectedAnalyses.length > 1 ? 's' : ''})
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="py-6 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <i className="ri-test-tube-line text-3xl text-gray-300"></i>
              <p className="text-sm text-gray-500 mt-2">Demandez des analyses de laboratoire pour ce patient.</p>
              {!suiteTerminee && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-3 px-4 py-1.5 bg-clinic-blue/10 text-clinic-blue rounded-lg hover:bg-clinic-blue/15 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-add-line mr-1"></i>Créer un bon de laboratoire
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}